/*
This file is part of the Notesnook project (https://notesnook.com/)

Copyright (C) 2023 Streetwriters (Private) Limited

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU General Public License for more details.

You should have received a copy of the GNU General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import "./polyfills";
import "./app.css";
// KaTeX CSS: 在应用启动时加载，确保首屏渲染前 CSS 已就绪
// 替代 CDN 加载（移动端 jsdelivr 易超时）
import "katex/dist/katex.min.css";
import { AppEventManager, AppEvents } from "./common/app-events";
import { register, unregister } from "./service-worker-registration";
import { getServiceWorkerVersion } from "./utils/version";
import { register as registerStreamSaver } from "./utils/stream-saver/mitm";
import { ThemeDark, ThemeLight, themeToCSS } from "@notesnook/theme";
import Config from "./utils/config";
import { setI18nGlobal, Messages } from "@notesnook/intl";
import { i18n } from "@lingui/core";
import { recoverFromStaleAppAssetError } from "./utils/stale-app-reload";
import { markStartupPhase } from "./utils/startup-rum";

markStartupPhase("entry");

const colorScheme = JSON.parse(
  window.localStorage.getItem("colorScheme") || '"light"'
);
const root = document.querySelector("html");
if (root) root.setAttribute("data-theme", colorScheme);

const theme =
  colorScheme === "dark"
    ? Config.get("theme:dark", ThemeDark)
    : Config.get("theme:light", ThemeLight);
const stylesheet = document.getElementById("theme-colors");
if (theme) {
  const css = themeToCSS(theme);
  if (stylesheet) stylesheet.innerHTML = css;
} else stylesheet?.remove();

// 检测浏览器语言
const detectedLanguage = navigator.language.split("-")[0]; // 例如 "zh", "en", "ja"
const supportedLocales = ["en", "zh"]; // 支持的语言列表
const userLocale = supportedLocales.includes(detectedLanguage) ? detectedLanguage : "en";

const locale = userLocale === "zh"
  ? import("@notesnook/intl/locales/$zh.json")
  : import("@notesnook/intl/locales/$en.json");

function markStartupError(error: unknown) {
  markStartupPhase("startup_error", {
    errorName: error instanceof Error ? error.name : "NonError",
    errorMessage: error instanceof Error ? error.message : String(error)
  });
}

locale.then(({ default: messages }) => {
  markStartupPhase("locale_loaded");
  i18n.load({
    [userLocale]: messages.messages as unknown as Messages
  });

  i18n.activate(userLocale);

  performance.mark("import:root");
  markStartupPhase("root_import_start");
  return import("./root").then(({ startApp }) => {
    markStartupPhase("root_import_done");
    performance.mark("start:app");
    markStartupPhase("start_app");
    return startApp();
  });
}).catch((error) => {
  if (recoverFromStaleAppAssetError(error)) return;
  markStartupError(error);
  throw error;
});
setI18nGlobal(i18n);

if (!IS_DESKTOP_APP) {
  //   logger.info("Initializing service worker...");

  // If you want your app to work offline and load faster, you can change
  // unregister() to register() below. Note this comes with some pitfalls.
  // Learn more about service workers: https://bit.ly/CRA-PWA

  // PWA 默认关闭；需要时通过 VITE_ENABLE_PWA=true 显式开启。
  if (import.meta.env.PROD && import.meta.env.VITE_ENABLE_PWA === "true") {
    register({
      onUpdate: async (registration: ServiceWorkerRegistration) => {
        if (!registration.waiting) return;
        const { formatted } = await getServiceWorkerVersion(registration.waiting);
        AppEventManager.publish(AppEvents.updateDownloadCompleted, {
          version: formatted
        });
      },
      onSuccess() {
        registerStreamSaver();
      }
    });
  } else {
    unregister();
  }

  // window.addEventListener("beforeinstallprompt", () => showInstallNotice());
}
